<template>
  <v-container fluid class="pa-0">
    <DataTable
      v-show="!activitiesLoading"
      :items.sync="entityActivities"
      :headers="activityHeaders"
      :itemClass="rowClass"
      showExpand
      :actions="actions"
      hideHeader
      @create="$emit('create')"
      @refresh="$emit('refresh')"
      tableKey="activityId"
      :customCells="customColumns"
      containerClass="fit-to-panel"
      expandable
      :expandOnCreation="!showActions"
      htmlIdentifier="entity-activity-table"
      :tableHeightCalculationData="tableHeightCalculationData"
      @expanded="expandedChanged"
    >
      <template #status="{item: activity}">
        <v-row no-gutters class="flex-nowrap" justify="center" align="center">
          <v-col cols="auto">
            <Icon
            :small="false"
            :icon="IconHelpers.getIconForObjectType(accountingCategoryFromInt(activity?.activityTemplate?.category))"
            :tooltipText="accountingCategoryFromInt(activity?.activityTemplate?.category)"/>
          </v-col>

          <v-col cols="auto">
            <Icon
            v-if="activityOrDetailsHasScheduledChange(activity)"
            small
            :icon-color="showActions ? 'primary' : 'info'"
            :icon="'invoice-text-update'"
            :tooltipText="$t('hasScheduledRateChange')"
            @icon-clicked="onScheduledRateChangeIconClicked"/>
          </v-col>

          <v-col cols="auto">
            <Icon
            v-if="activity?.activityTemplate?.costType !== 0"
            small
            icon-color="info"
            :icon="costIcons[activity?.activityTemplate?.costType]"
            :tooltipText="CostTypeFromTemplate(activity?.activityTemplate)" />
          </v-col>

          <v-col cols="auto">
            <Icon
            v-if="activity?.recoveryMode !== RecoveryMode.None.value"
            small
            :itemClass="rowClass"
            icon="mdi-cash-multiple"
            icon-color="success"
            :tooltipText="recoveryModeString(activity)" />
          </v-col>

          <v-col cols="auto">
            <Icon
            v-if="activity?.deductFromActivityId"
            icon="mdi-circle-multiple-outline"
            :tooltipText="deductionTooltip(activity)"
            small
            icon-color="success"
            @mouseenter.native="setHoveredParent(activity.deductFromActivityId)"
            @mouseleave.native="clearHoveredParent"
            />
            <Icon
            v-if="hasDeductingChildren(activity?.activityId)"
            icon="mdi-minus-circle-multiple-outline"
            :tooltipText="deductionParentTooltip(activity)"
            small
            icon-color="error"
            @mouseenter.native="setHoveredDeducting(activity?.activityId)"
            @mouseleave.native="clearHoveredDeducting"
            />
          </v-col>
          <v-col cols="auto">
            <Icon
            v-if="TemplateSpecialization.forInt(activity?.activityTemplate?.specialization)?.icon"
            :icon="TemplateSpecialization.forInt(activity?.activityTemplate?.specialization)?.icon"
            :tooltipText="TemplateSpecialization.forInt(activity?.activityTemplate?.specialization)?.name"
            :small="false"/>
          </v-col>
        </v-row>
      </template>

      <template #activity-name=" { item: activity }">
        <span>{{activity?.activityTemplate?.name}}</span>
      </template>

      <template #base-cost-rate="{ item: activity }">
        <v-row no-gutters class="flex-nowrap" justify="end" align="center">
          <v-col cols="auto" v-if="activityHasScheduledChange(activity)">
            <Icon
            icon="invoice-text-update"
            :icon-color="showActions ? 'primary' : 'info'"
            :tooltipText="scheduledRateChangeTooltip(activity)"
            margin="mb-1 mr-1"
            @icon-clicked="onScheduledRateChangeIconClicked"/>
          </v-col>

          <v-col cols="auto" v-if="!!activity.baseCost?.rateSheetId">
            <Icon
            icon="mdi-note-text"
            margin="mb-1 mr-1"
            :tooltipText="`${$t('rateSheet')}: ${activity.baseCost.rateSheet.name}`"
            @icon-clicked="rateSheetDetailClicked(activity)"/>
          </v-col>

          <v-col  v-if="activity.baseCost !== null" cols="auto">
            <span>
              {{ formatMoney(activity.baseCost.rate) }}
            </span>
          </v-col>

          <v-col v-else cols="auto">
            <span>
              {{ $t('notAvailable') }}
            </span>
          </v-col>
        </v-row>
      </template>

      <template #gl-offset="{ item: activity }">
        {{activity?.activityTemplate?.glOffset ? activity.activityTemplate.glOffset : $t('notAvailable')}}
      </template>

      <template #base-cost-pay-on="{ item: activity }">
        <span v-if="activity?.baseCost !== null">
        {{payOnFromInt(activity.baseCost.payOn)}}
        </span>
        <span v-else>
          {{$t('notApplicable')}}
        </span>
      </template>

      <template #base-cost-pay-by="{ item: activity }">
        <span>
        {{ payByFromItem(activity) }}
        </span>
      </template>

      <template #actions="{ item: activity }">
        <v-row no-gutters class="flex-nowrap" justify="center">
          <v-col cols="auto">
            <Icon
            margin="mr-2"
            icon="mdi-pencil"
            icon-color="success"
            elementId="edit-activity"
            :tooltipText="$t('editActivity')"
            @icon-clicked="emitTableAction(activity, 'edit')"/>
          </v-col>

          <v-col cols="auto">
            <Icon
            v-if="showAddDetail(activity)"
            margin="mr-2"
            icon="mdi-plus"
            icon-color="success"
            elementId="new-detail"
            :tooltipText="$t('createDetail')"
            @icon-clicked="createDetail(activity)"/>
          </v-col>

          <v-col cols="auto">
            <Icon
            icon="mdi-delete-forever"
            icon-color="error"
            elementId="delete-activity"
            :tooltipText="$t('deleteActivity')"
            :disabled="activity.activityDetails && activity.activityDetails.length !== 0"
            @icon-clicked="emitTableAction(activity, 'delete')"/>
          </v-col>
        </v-row>
      </template>

      <template #counterparty="{ item: activity }">
        <Icon
        v-if="showInsuranceExpiryIcon(activity)"
        icon="mdi-shield-alert"
        :tooltipText="getInsuranceExpiryTooltip(activity)"/>

        <span v-if="counterpartySet(activity)">
          <Icon
          iconColor="info"
          v-if="activity?.runtimeCounterparty !== null"
          icon="mdi-alpha-r-circle-outline"
          :tooltipText="`${$t('runtimeCounterparty')}: ${runtimeCounterparty(activity?.runtimeCounterparty)}`"/>
          {{ counterpartyString(activity) }}
        </span>

        <span v-else>
          {{ $t('notSet') }}
          <Icon
          v-if="activity?.runtimeCounterparty !== null"
          icon="mdi-alert"
          iconColor="error"
          tooltipTcolor="error"
          :tooltipText="$t('runtimeFieldNotSpecified', { runtimeField: runtimeCounterparty(activity?.runtimeCounterparty) })"/>
        </span>
      </template>

      <template #details="{ item: activity }">
        <v-row no-gutters align="center">
          <v-col class="mt-1" :style="{ maxWidth: `${detailChipContainerWidth}px` }">
            <v-chip small class="mb-1 mr-1" v-for="detail in activity.activityDetails.slice(0, numDetailChipsToDisplay)" :key="detail.activityDetailId">
              <span style="overflow: hidden;">
                {{detail.product.name}}
              </span>

              <span v-if="detailHasScheduledChange(detail, activity)">
                <Icon
                icon="invoice-text-update"
                :icon-color="showActions ? 'primary' : 'info'"
                margin="ml-2"
                :tooltipText="getScheduledRateChangeTooltipForDetail(detail, activity)"
                @icon-clicked="onScheduledRateChangeIconClicked"/>
              </span>

              <span class="ml-1">
                {{formatMoney(detail.cost.rate)}}
              </span>
            </v-chip>
          </v-col>
        </v-row>
      </template>

      <template #expanded-item="{ item: activity }">
        <v-container class="px-0" v-if="!showActions && displayActivityDetails(activity)" fluid>
          <v-row dense>
            <v-col cols="12" sm="6" md="3" xl="auto"
            v-for="detail in activity.activityDetails.toSorted((a, b) => a.product.name.localeCompare(b.product.name))"
            :key="detail.activityDetailId">
              <v-card outlined class="fill-height" :style="{ minWidth: 'max(12vw, 200px)' }">
                <v-card-title>
                  <v-row no-gutters align="center" class="mt-n1">
                    <v-col>
                      <span class="subtitle-1">{{detail.product.name}}</span>
                    </v-col>
                    <v-col cols="auto">
                      <Icon
                      v-if="detailHasScheduledChange(detail, activity)"
                      icon="invoice-text-update"
                      :icon-color="showActions ? 'primary' : 'info'"
                      margin="mx-2"
                      :tooltipText="getScheduledRateChangeTooltipForDetail(detail, activity)"
                      @icon-clicked="onScheduledRateChangeIconClicked"/>

                      <span class="v-card__text pa-0">{{formatMoney(detail.cost.rate)}}</span>
                    </v-col>
                  </v-row>
                </v-card-title>
              </v-card>
            </v-col>
          </v-row>
        </v-container>

        <v-container v-else-if="displayActivityDetails(activity)">
          <v-card style="max-width: 600px;" outlined>
            <ActivityDetailTable
              :contract="contract"
              :showActions="showActions"
              :activity="activity"
              @detail-deleted="$emit('refresh')"
              @edit-detail="editDetail"
            >
            </ActivityDetailTable>
          </v-card>
        </v-container>

        <v-container v-else-if="displayActivityVolumeInformation(activity)">
          <ActivityVolumeInformation
          :activity="activity"
          :destinationAccount="destinationAccount"
          @rate-sheet-detail-clicked="rateSheetDetailClicked"/>
        </v-container>
      </template>
    </DataTable>

    <Dialog :stateId="dialogId" @close="dialogClosed" @dialog-closing="dialogClosed" @click:outside="dialogClosed">
      <RateSheetViewer
      v-if="viewingRateSheetDetails"
      :rateSheet="viewingRateSheetDetails"
      :contract="contract"
      :activity="focusedActivity"
      :selectedDate="selectedDate"
      :accountId="purchasingAccountId"/>
    </Dialog>
  </v-container>
</template>

<script>
import { PayOn, PayBy, AccountingCategory, CostType, RuntimeCounterparty, RecoveryMode, TemplateSpecialization, ProductionCostType } from '@/utils/Enumerations'
import { formatMoney } from '@/utils/NumericMutations.js'
import { mapGetters, mapActions } from 'vuex'
import ActivityHeaders from '@/headers/Activity.js'
import { TemplateSpecializationInfo } from '@/components/activity/ActivityMapping'
import { companyOffsetToLocalDate } from '@/utils/DateFormatter.js'
import moment from 'moment'
import { uniqueDialogId } from '@/utils/componentHelpers'
import IconHelpers from '@/utils/IconHelpers'

export default {
  name: 'EntityActivityTable',

  props: {
    showActions: {
      type: Boolean,
      default: false
    },
    contract: {
      type: Object,
      default: undefined
    },
    businessEntityId: {
      type: Number,
      default: undefined
    },
    multiEntity: {
      type: Boolean,
      default: true
    },
    allActivities: {
      type: Array,
      default: undefined
    },
    tract: {
      type: Object,
      default: undefined
    },
    selectedDate: {
      type: String,
      default: null
    },
    tableHeightCalculationData: {
      type: Object,
      required: false,
      default: () => ({
        offset: 500
      })
    }
  },

  components: {
    ActivityDetailTable: () => import('@/components/activity/activity-detail/DetailTable.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    RateSheetViewer: () => import('@/components/activity/rate-sheet/RateSheetViewer.vue'),
    ActivityVolumeInformation: () => import('@/components/activity/ActivityVolumeInformation.vue')
  },

  data: () => ({
    PayBy,
    TemplateSpecializationInfo,
    TemplateSpecialization,
    RecoveryMode,
    IconHelpers,
    hoveredDeductingActivityIds: [],
    hoveredParentActivityId: null,
    dialogId: uniqueDialogId('entity-activity-table-dialog'),
    viewingRateSheetDetails: undefined,
    rateSheetsByActivityDetailId: new Map(),
    focusedActivity: undefined,
    destinationAccountFetched: false,
    destinationAccount: undefined,
    tractGroup: undefined
  }),

  computed: {
    ...mapGetters('activity', ['activitiesLoading']),
    ...mapGetters('scheduled-rate-change', ['contractScheduledRateChanges']),
    ...mapGetters('user', ['businessEntities']),

    entityNameById () {
      return this.businessEntities.reduce((acc, entity) => {
        acc[entity.businessEntityId] = entity.name
        return acc
      }, {})
    },

    activityHeaders () {
      return !this.showActions
        ? ActivityHeaders.activityNoCRUDHeaders()
        : ActivityHeaders.activityCRUDHeaders().filter(h => h.value !== 'actions')
    },

    customColumns () {
      return [
        {
          slotName: 'status',
          value: 'status'
        },
        {
          slotName: 'activity-name',
          value: 'activityTemplate.name'
        },
        {
          slotName: 'base-cost-rate',
          value: 'baseCost.rate'
        },
        {
          slotName: 'gl-offset',
          value: 'activityTemplate.glOffset'
        },
        {
          slotName: 'base-cost-pay-on',
          value: 'baseCost.payOn'
        },
        {
          slotName: 'base-cost-pay-by',
          value: 'baseCost.payBy'
        },
        {
          slotName: 'actions',
          value: 'actions'
        },
        {
          slotName: 'counterparty',
          value: 'contractor.name'
        },
        {
          slotName: 'details',
          value: 'details'
        }
      ]
    },

    actions () {
      if (!this.showActions) {
        return []
      } else {
        return [
          {
            actionName: 'refresh',
            icon: 'mdi-refresh',
            text: this.$t('refresh')
          },
          {
            actionName: 'create',
            icon: 'mdi-plus',
            text: this.$t('new'),
            id: 'new-activity'
          }
        ]
      }
    },

    costIcons () {
      return ['mdi-alpha-u-circle-outline', 'mdi-alpha-p-circle-outline', 'mdi-alpha-s-circle-outline', 'mdi-alpha-n-circle-outline']
    },

    entityActivities () {
      const activities = this.multiEntity
        ? this.allActivities.filter(activity => parseInt(activity?.activityTemplate?.businessEntityId) === this.businessEntityId)
        : this.allActivities
      return activities.map(a => ({
        ...a,
        businessEntityName: this.entityNameById[a?.activityTemplate?.businessEntityId] ?? '',
        isExpandable: a.activityDetails.length > 0 || (a.baseCost?.payBy === PayBy.Volume.value && a.namedRateId == null)
      }))
    },

    numDetailChipsToDisplay () {
      return Math.max(2, this.$vuetify.breakpoint.width * 0.0012)
    },

    detailChipContainerWidth () {
      return Math.max(200, this.$vuetify.breakpoint.width * 0.12)
    },

    purchasingAccountId () {
      if (this.tract?.tractGroupId == null) return this.contract.destinationAccountId
      return this.businessEntities.find(be => be.businessEntityId === this.tractGroup?.businessEntityId)?.accountId
    }
  },

  methods: {
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('tract', ['fetchTract', 'fetchTractGroup']),
    ...mapActions('rate-sheet', ['fetchRateSheet']),
    ...mapActions('account', ['fetchAccountById']),
    ...mapActions('user', ['fetchAllBusinessEntities']),

    formatMoney,
    companyOffsetToLocalDate,
    accountingCategoryFromInt: (ac) => AccountingCategory.fromInt(ac),
    CostTypeFromTemplate (template) {
      const costType = CostType.forInt(template.costType)
      if (costType !== CostType.Production) return costType.name

      const productionCostType = ProductionCostType.forInt(template.productionType)
      return productionCostType === ProductionCostType.Other
        ? costType.name
        : `${costType.name} - ${productionCostType.name}`
    },
    expand (activity) {
      if (this.expandedActivities.includes(activity)) {
        this.expandedActivities = []
      } else {
        this.expandedActivities = [activity]
      }
    },

    emitTableAction (activity, actionType) {
      const event = {
        activity: activity,
        type: actionType
      }
      this.$emit('table-event', event)
    },

    createDetail (activity) {
      this.emitTableAction(activity.activityId, 'new-detail')
    },

    editDetail (detail) {
      this.emitTableAction(detail, 'edit-detail')
    },

    payByFromItem ({ baseCost, activityTemplate: { specialization, category } }) {
      if (specialization === TemplateSpecialization.Penalty.value && category !== AccountingCategory.Receivable.value) return PayBy.Load.name
      if (baseCost?.payBy == null) return this.$t('notApplicable')
      return PayBy.fromInt(baseCost.payBy)
    },

    payOnFromInt (val) {
      return PayOn.fromInt(val)
    },

    recoveryModeString (val) {
      return this.$t('recoverAdvancesWithDetail', {
        detail: val.advanceNotes ?? RecoveryMode.fromInt(val.recoveryMode)
      })
    },

    hasDeductingChildren (activityId) {
      return this.allActivities?.some(a => a.deductFromActivityId === activityId)
    },

    getDeductingChildren (activityId) {
      return (this.allActivities || []).filter(a => a.deductFromActivityId === activityId)
    },

    setHoveredDeducting (parentActivityId) {
      this.hoveredDeductingActivityIds = this.getDeductingChildren(parentActivityId).map(a => a.activityId)
    },

    clearHoveredDeducting () {
      this.hoveredDeductingActivityIds = []
    },

    setHoveredParent (parentActivityId) {
      this.hoveredParentActivityId = parentActivityId
    },

    clearHoveredParent () {
      this.hoveredParentActivityId = null
    },

    deductionParentTooltip (item) {
      const children = this.getDeductingChildren(item.activityId)
      const tooltipCount = 2
      if (children.length === 0) return ''
      const names = children.slice(0, tooltipCount)
        .map(c => `${c?.activityTemplate?.name} - (${this.counterpartyString(c)})`)
        .join(', ')
      const more = children.length > tooltipCount ? `, +${children.length - tooltipCount} more` : ''
      return `Deducted by: ${names}${more}`
    },

    rowClass (item) {
      const classes = []
      if (this.hoveredDeductingActivityIds.includes(item.activityId)) classes.push('activity-row-red-tint')
      if (this.hoveredParentActivityId === item.activityId) classes.push('activity-row-green-tint')
      return classes.join(' ')
    },

    counterpartySet (activity) {
      return activity?.runtimeCounterparty === RuntimeCounterparty.Driver.value ||
        activity?.contractor !== null ||
        (activity?.runtimeCounterparty === RuntimeCounterparty.Landowner.value && this.tract?.landownerCount > 0)
    },

    counterpartyString (activity) {
      if (activity?.runtimeCounterparty === RuntimeCounterparty.Driver.value) return this.$t('ticketDriver')
      return activity?.contractor?.name ?? (this.tract?.landOwnerAccountName || this.$t('multiple'))
    },

    runtimeCounterparty: (val) => RuntimeCounterparty.fromInt(val),

    showAddDetail (item) {
      const isNotTransfer = item.activityTemplate.specialization !== TemplateSpecialization.Transfer.value
      const isNotApOrJePenalty = item.activityTemplate.specialization !== TemplateSpecialization.Penalty.value || item.activityTemplate.category === AccountingCategory.Receivable.value
      const isNotByVolume = item.baseCost?.payBy !== PayBy.Volume.value
      return isNotTransfer && isNotApOrJePenalty && isNotByVolume
    },

    showInsuranceExpiryIcon (item) {
      if (item?.contractor?.insuranceExpiryDate == null) return false
      const contractorInsuranceExpiryDateAsLocal = companyOffsetToLocalDate(item.contractor.insuranceExpiryDate, 'MM/DD/YYYY')
      const contractorInsuranceIsExpired = moment(contractorInsuranceExpiryDateAsLocal)
        .isSameOrBefore(moment().endOf('day'))
      return contractorInsuranceIsExpired
    },

    getInsuranceExpiryTooltip (item) {
      return `${this.$t('expiredOn')} ${companyOffsetToLocalDate(item.contractor.insuranceExpiryDate, 'MM/DD/YYYY')}`
    },

    deductionTooltip (item) {
      const parentActivity = this.allActivities.find(a => a.activityId === item.deductFromActivityId)
      const counterpartyString = this.counterpartyString(parentActivity)
      const activityCounterpartyString = `${parentActivity?.activityTemplate?.name} - (${counterpartyString})`
      return `${this.$t('deductsFrom', { from: activityCounterpartyString })}`
    },

    async rateSheetDetailClicked (activity) {
      this.focusedActivity = activity
      const sheetResponse = await this.fetchRateSheet(activity.baseCost.rateSheetId)
      if (this.tract?.tractGroupId != null) {
        this.tractGroup = await this.fetchTractGroup(this.tract.tractGroupId)
      }

      if (this.businessEntities.length === 0) {
        await this.fetchAllBusinessEntities()
      }

      this.viewingRateSheetDetails = sheetResponse
      this.openDialog()
    },

    dialogClosed () {
      this.viewingRateSheetDetails = undefined
      this.focusedActivity = undefined
      this.closeDialogsAtOrAbove(this.dialogId)
    },

    openDialog () {
      this.openOrUpdateDialog({ id: this.dialogId, width: '85vw' })
    },

    displayActivityDetails (activity) {
      return activity.baseCost?.payBy !== PayBy.Volume.value
    },

    displayActivityVolumeInformation (activity) {
      return activity.baseCost?.payBy === PayBy.Volume.value && activity.namedRateId == null
    },

    expandedChanged (expanded) {
      const isVolume = expanded.some(a => a?.baseCost?.payBy === PayBy.Volume.value)
      if (isVolume) {
        this.fetchAccountDetails()
      }
    },

    async fetchAccountDetails () {
      if (this.destinationAccountFetched || !this.contract?.destinationAccountId) return
      this.destinationAccountFetched = true
      const account = await this.fetchAccountById(this.contract.destinationAccountId)
      this.destinationAccount = account
    },

    activityHasScheduledChange (activity) {
      if (!activity.baseCost) {
        return false
      }
      return this.contractScheduledRateChanges.activityRateChanges?.some(csrc => {
        return activity.activityId === csrc.activityId && activity.baseCost.rate !== csrc.newRate
      })
    },

    detailHasScheduledChange (detail, activity) {
      if (!detail.cost) {
        return false
      }
      return this.contractScheduledRateChanges.activityRateChanges?.some(csrc => {
        return activity.activityId === csrc.activityId &&
        csrc.newDetailRates?.some(d => d.productId === detail.product.productId) &&
        detail.cost.rate !== csrc.newDetailRates.find(d => d.productId === detail.product.productId)?.newRate
      })
    },

    scheduledRateChangeTooltip (activity) {
      const change = this.contractScheduledRateChanges.activityRateChanges.find(csrc => {
        return activity.activityId === csrc.activityId
      })
      if (!change) return this.$t('hasScheduledRateChange')
      const result = `${this.$t('hasScheduledRateChange')}: ${companyOffsetToLocalDate(this.contractScheduledRateChanges?.effectiveDate, 'MM/DD/YYYY')} - ${formatMoney(change.newRate)}`
      return result
    },

    getScheduledRateChangeTooltipForDetail (detail, activity) {
      const changedActivity = this.contractScheduledRateChanges.activityRateChanges.find(csrc => {
        return activity.activityId === csrc.activityId && csrc.newDetailRates?.some(d => d.productId === detail.product.productId)
      })
      const changedDetail = changedActivity?.newDetailRates.find(d => d.productId === detail.product.productId)
      if (!changedActivity) return this.$t('hasScheduledRateChange')
      const result = `${this.$t('hasScheduledRateChange')}: ${companyOffsetToLocalDate(this.contractScheduledRateChanges?.effectiveDate, 'MM/DD/YYYY')} - ${formatMoney(changedDetail?.newRate)}`
      return result
    },

    activityOrDetailsHasScheduledChange (activity) {
      if (this.activityHasScheduledChange(activity)) {
        return true
      }
      const detailHasScheduledChange = activity.activityDetails.some(d => this.detailHasScheduledChange(d, activity))
      const activityRateChange = this.contractScheduledRateChanges?.activityRateChanges?.find(csrc => csrc.activityId === activity.activityId)
      const hasNewDetail = activityRateChange?.newDetailRates.find(ndr => activity.activityDetails.every(ad => ad.product.productId !== ndr.productId))
      return detailHasScheduledChange || !!hasNewDetail
    },

    onScheduledRateChangeIconClicked () {
      this.$emit('scheduled-rate-change-clicked')
    }
  }
}
</script>

<style>
.v-application .fit-to-panel {
  padding: 1px;
  border-radius: 0px 4px 4px 0px;
  overflow: clip;
}

.entity-activity-table-table-container .v-data-table__wrapper table tbody tr.activity-row-red-tint > td {
  background-color: rgba(244, 67, 54, 0.12);
}

.entity-activity-table-table-container .v-data-table__wrapper table tbody tr.activity-row-green-tint > td {
  background-color: rgba(76, 175, 80, 0.12);
}
</style>
